import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import { useAppTranslation } from "../../../hooks/useAppTranslation";
import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  TextField,
  Box,
  Tooltip,
  Grid,
  Autocomplete,
  Popper,
  IconButton,
  Menu,
  MenuItem,
  Badge,
} from "@mui/material";
import { DatePicker, pickersLayoutClasses } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import useFetchUsersList from "../../../hooks/useFetchUsersList";
import useFetchAllSystem from "../../../hooks/useFetchAllSystem";
import { useSystemContext } from "../../../contexts/TagAndSystemProvider";
import AutoCompleteDomainList from "../Domain/AutoCompleteDomainList";
import AutoCompleteRuleList from "../Rule/AutoCompleteRuleList";
import AutoCompleteResourceList from "../Resource/AutoCompleteResourceList";
import AutoCompleteRunList from "../Run/AutoCompleteRunList";
import AutoCompleteExecutionSubGroup from "../ExecutionSubGroup/AutoCompleteExecutionSubGroup";
import AutoCompleteComparisonResearchQueryId from "../ComparisonResearchQuery/AutoCompleteComparisonResearchQueryId";
import AutoCompleteGenericResearchQueryId from "../GenericResearchQuery/AutoCompleteGenericResearchQueryId";
import AutocompleteStatusField from "../Status/AutocompleteStatusField";
import SystemFilterDropdown from "../System/SystemFilterDropdown";
import { IconMenus } from "../../../common/utils/icons";
import {
  issueStatus,
  batchReasonType,
  systemReasonType,
  falseReasonType,
  scoringSeverityOptions,
} from "../../../services/constants";
import { useClassificationContext } from "../../../contexts/ClassificationContext";
import useFetchReasonsByClassification from "../../../hooks/useFetchReasonsByClassification";

// Types
interface IFilterData {
  end_date: string;
  start_date: string;
  assigned_user: string;
  assigned_by: string;
  domain_id: string;
  domain_name: string; // ✅ ADDED: New field for domain name search
  rule_id: string;
  resource_id: string;
  execution_id: string;
  rule_name: string;
  run: string;
  run_id: string; // ✅ ADDED: Include run_id in interface
  status: string;
  classification: string;
  reason: string;
  issue_classification_id: number | null;
  issue_reason_id: number | null;
  research_query_id: string;
  query_name: string;
  generic_research_id: string | number; // ✅ ADDED: New field
  generic_research_code: string | number; // ✅ ADDED: New field
  generic_execution_id: string | number; // ✅ ADDED: New field
  generic_research_query_id: string; // ✅ ADDED: New field for clear filter functionality
  execution_status: string; // ✅ ADDED: New field
  comparison_research_query_id: string; // ✅ ADDED: New field for comparison research query filter
  status_filter: string; // ✅ ADDED: New field for status filter (Active, Inactive, All)
  query_type_filter: string; // ✅ ADDED: New field for query type filter (Resource, External, Mixed)
  system_id: string | number; // ✅ ADDED: New field for system filter
  resource_type: string; // ✅ ADDED: New field for file processing resource type search
  run_name: string; // ✅ ADDED: New field for execution group name search
  domain_linkage_ids: any[]; // ✅ ADDED: New field for domain linkage IDs filter (multi-select)
  source_domain_id: any[]; // ✅ UPDATED: Changed to array for multi-select
  target_domain_id: any[]; // ✅ UPDATED: Changed to array for multi-select
  source_domain_key: string; // ✅ ADDED: New field for source domain key filter
  target_domain_key: string; // ✅ ADDED: New field for target domain key filter
  username: string; // ✅ ADDED: New field for username search
  email: string; // ✅ ADDED: New field for email search
  role_name: string; // ✅ ADDED: New field for role name search
  group_name: string; // ✅ ADDED: New field for group name search
  tag_name: string; // ✅ ADDED: New field for tag name search
  system_name: string; // ✅ ADDED: New field for system name search
  connection_runtime_id: string; // ✅ ADDED: New field for connection runtime config ID search
  code: string; // ✅ ADDED: New field for connection runtime config code search
  connection_type: string; // ✅ ADDED: New field for connection runtime config type search
  run_instance_id: string; // ✅ ADDED: New field for execution group ID filter
  name: string; // ✅ ADDED: New field for resource column name search
  resource_column_filter_ids: string; // ✅ ADDED: New field for resource column filter IDs (comma-separated)
  comparison_research_query_available: boolean | null | string; // ✅ ADDED: New field for comparison research query availability filter (can be boolean, null, or string)
  severity: string; // ✅ ADDED: New field for scoring severity filter (low/medium/high)
  execution_sub_group_id: string | number; // ✅ ADDED: New field for execution sub group filter
  [key: string]: any;
}

interface IFieldRequirements {
  dateTitle?: string;
  tooltipRequired?: boolean;
  placeholder?: string;
  queryNamePlaceholder?: string; // ✅ NEW: Custom placeholder for query_name field
  namePlaceholder?: string; // ✅ NEW: Custom placeholder for name field (resource column name)
  includeDomainId?: boolean; // ✅ Controls whether to include domain ID in display
  includeRuleId?: boolean; // ✅ Controls whether to include rule ID in display
  includeRunId?: boolean; // ✅ Controls whether to include run ID in display
  includeResourceId?: boolean; // ✅ NEW: Controls whether to include resource ID in display
  includeComparisonResearchQueryId?: boolean; // ✅ NEW: Controls whether to include comparison research query ID in display
  includeStatusFilter?: boolean; // ✅ NEW: Controls whether to include status filter in display
  includeSystemId?: boolean; // ✅ NEW: Controls whether to include system ID in display
  enableDomainResourceCascading?: boolean; // ✅ NEW: Enable cascading filter from domain to resource
  defaultStatusFilter?: string; // ✅ NEW: Default status filter value to reset to on clear (e.g., "Active")
  hasMultipleRuleIds?: boolean; // ✅ NEW: Enable multiple selection for rule_list filter
  hasMultipleComparisonResearchQueryIds?: boolean; // ✅ NEW: Enable multiple selection for comparison_research_query_id filter
}

interface ISearchFilterBarProps {
  className?: string;
  largeWidth?: boolean;
  setSearchFilterData: Dispatch<SetStateAction<any>>;
  searchFilterData: any;
  requiredFields?: Array<
    | "end_date"
    | "start_date"
    | "assigned_user"
    | "assigned_by"
    | "domain_list"
    | "domain_name"
    | "resource_list"
    | "rule"
    | "rule_list"
    | "run_list"
    | "run_id"
    | "execution_id"
    | "rule_id"
    | "rule_name"
    | "status"
    | "classification"
    | "reason"
    | "generic_research_id"
    | "generic_research_code"
    | "generic_execution_id"
    | "generic_research_query_id"
    | "execution_status"
    | "query_name"
    | "name" // ✅ ADDED: New field for resource column name search
    | "resource_column_filter_ids" // ✅ ADDED: New field for resource column filter IDs
    | "comparison_research_query_id"
    | "comparison_research_query_available"
    | "status_filter"
    | "query_type_filter"
    | "system_id"
    | "resource_type"
    | "run_name"
    | "domain_linkage_ids"
    | "source_domain_id"
    | "target_domain_id"
    | "source_domain_key"
    | "target_domain_key"
    | "username"
    | "email"
    | "role_name"
    | "group_name"
    | "tag_name"
    | "system_name"
    | "connection_runtime_id"
    | "code" // ✅ ADDED: New field for connection runtime config code search
    | "connection_type" // ✅ ADDED: New field for connection runtime config type search
    | "run_instance_id" // ✅ ADDED: New field for execution group ID filter
    | "issue_classification_name" // ✅ ADDED: New field for classification name search
    | "reason_name" // ✅ ADDED: New field for reason name search
    | "severity" // ✅ ADDED: Scoring severity filter
    | "execution_sub_group_list" // ✅ ADDED: Execution sub group filter
  >;
  primaryFields?: Array<
    | "end_date"
    | "start_date"
    | "assigned_user"
    | "assigned_by"
    | "domain_list"
    | "domain_name"
    | "resource_list"
    | "rule"
    | "rule_list"
    | "run_list"
    | "run_id"
    | "execution_id"
    | "rule_id"
    | "rule_name"
    | "status"
    | "classification"
    | "reason"
    | "generic_research_id"
    | "generic_research_code"
    | "generic_execution_id"
    | "generic_research_query_id"
    | "execution_status"
    | "query_name"
    | "name" // ✅ ADDED: New field for resource column name search
    | "resource_column_filter_ids" // ✅ ADDED: New field for resource column filter IDs
    | "comparison_research_query_id"
    | "comparison_research_query_available"
    | "status_filter"
    | "query_type_filter"
    | "system_id"
    | "resource_type"
    | "run_name"
    | "domain_linkage_ids"
    | "source_domain_id"
    | "target_domain_id"
    | "source_domain_key"
    | "target_domain_key"
    | "username"
    | "email"
    | "role_name"
    | "group_name"
    | "tag_name"
    | "system_name"
    | "connection_runtime_id"
    | "code" // ✅ ADDED: New field for connection runtime config code search
    | "connection_type" // ✅ ADDED: New field for connection runtime config type search
    | "run_instance_id" // ✅ ADDED: New field for execution group ID filter
    | "issue_classification_name" // ✅ ADDED: New field for classification name search
    | "reason_name" // ✅ ADDED: New field for reason name search
    | "severity" // ✅ ADDED: Scoring severity filter
    | "execution_sub_group_list" // ✅ ADDED: Execution sub group filter
  >;
  fieldRequirements?: IFieldRequirements;
  currentPageType?: string;
  /**
   * DQ-8528 — When true the filter bar renders using MUI theme tokens instead
   * of the legacy SCSS class-based layout.  Defaults to false so all existing
   * callers are unaffected (AC-7).
   */
  useNewTheme?: boolean;
}

// Constants
const INITIAL_FILTER_DATA: IFilterData = {
  end_date: "",
  start_date: "",
  assigned_user: "",
  assigned_by: "",
  domain_id: "",
  domain_name: "", // ✅ ADDED: Include domain_name in initial data
  rule_id: "",
  resource_id: "",
  execution_id: "",
  rule_name: "",
  run: "",
  run_id: "", // ✅ ADDED: Include run_id in initial data
  status: "",
  classification: "",
  reason: "",
  issue_classification_id: null,
  issue_reason_id: null,
  research_query_id: "",
  query_name: "",
  generic_research_id: "",
  generic_research_code: "",
  generic_execution_id: "",
  generic_research_query_id: "", // ✅ ADDED: Include generic_research_query_id in initial data
  execution_status: "",
  comparison_research_query_id: "", // ✅ ADDED: Include comparison_research_query_id in initial data
  comparison_research_query_available: null, // ✅ ADDED: Boolean filter for rules with comparison research queries (true/false/null)
  status_filter: "", // ✅ ADDED: New field for status filter
  query_type_filter: "", // ✅ ADDED: New field for query type filter
  system_id: "", // ✅ ADDED: New field for system filter
  resource_type: "", // ✅ ADDED: New field for file processing resource type search
  run_name: "", // ✅ ADDED: New field for execution group name search
  domain_linkage_ids: [], // ✅ ADDED: New field for domain linkage IDs filter (multi-select)
  source_domain_id: [], // ✅ UPDATED: Changed to array for multi-select
  target_domain_id: [], // ✅ UPDATED: Changed to array for multi-select
  source_domain_key: "", // ✅ ADDED: New field for source domain key filter
  target_domain_key: "", // ✅ ADDED: New field for target domain key filter
  username: "", // ✅ ADDED: New field for username search
  email: "", // ✅ ADDED: New field for email search
  role_name: "", // ✅ ADDED: New field for role name search
  group_name: "", // ✅ ADDED: New field for group name search
  tag_name: "", // ✅ ADDED: New field for tag name search
  system_name: "", // ✅ ADDED: New field for system name search
  connection_runtime_id: "", // ✅ ADDED: New field for connection runtime config ID search
  code: "", // ✅ ADDED: New field for connection runtime config code search
  connection_type: "", // ✅ ADDED: New field for connection runtime config type search
  run_instance_id: "", // ✅ ADDED: New field for execution group ID filter
  name: "", // ✅ ADDED: New field for resource column name search
  resource_column_filter_ids: "", // ✅ ADDED: New field for resource column filter IDs (comma-separated)
  severity: "", // ✅ ADDED: Scoring severity filter
  execution_sub_group_id: "", // ✅ ADDED: Execution sub group filter
};

const SearchFilterBar: React.FC<ISearchFilterBarProps> = ({
  className,
  largeWidth,
  setSearchFilterData,
  searchFilterData,
  requiredFields = [],
  primaryFields = ["classification", "reason"], // Default primary fields
  currentPageType,
  fieldRequirements = {},
  useNewTheme = false, // DQ-8528: defaults to false for backward compat (AC-7)
}) => {
  const { t } = useAppTranslation();
  // Get dynamic classifications from context
  const { allClassifications } = useClassificationContext();
  const classificationOptions = allClassifications
    .filter((c: any) => c.is_active)
    .map((c: any) => c.issue_classification_name);

  // State
  const [filterData, setFilterData] =
    useState<IFilterData>(INITIAL_FILTER_DATA);
  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState<null | HTMLElement>(
    null,
  );

  // Get the classification ID for the currently selected classification name
  const selectedClassificationId =
    allClassifications.find(
      (c: any) => c.issue_classification_name === filterData?.classification,
    )?.id ?? null;

  // Fetch dynamic reason options based on selected classification ID
  const [dynamicReasons] = useFetchReasonsByClassification(
    selectedClassificationId,
  );
  const reasonOptions = dynamicReasons
    .filter((r: any) => r.is_active)
    .map((r: any) => r.reason_name);

  // Get the reason ID for the currently selected reason name
  const selectedReasonId =
    dynamicReasons.find((r: any) => r.reason_name === filterData?.reason)?.id ??
    null;

  // Sync with incoming searchFilterData
  useEffect(() => {
    if (searchFilterData) {
      const newFilterData = {
        end_date: searchFilterData.end_date || "",
        start_date: searchFilterData.start_date || "",
        assigned_user: searchFilterData.assigned_user || "",
        assigned_by: searchFilterData.assigned_by || "",
        domain_id: searchFilterData.domain_id || "",
        domain_name: searchFilterData.domain_name || "", // ✅ ADDED: Sync domain_name field
        rule_id: searchFilterData.rule_id || "",
        resource_id: searchFilterData.resource_id || "",
        execution_id: searchFilterData.execution_id || "",
        rule_name: searchFilterData.rule_name || "",
        run: searchFilterData.run || "", // Sync run field
        run_id: searchFilterData.run_id || "", // Sync run_id field
        status: searchFilterData.status || "",
        classification: searchFilterData.classification || "",
        reason: searchFilterData.reason || "",
        research_query_id: searchFilterData.research_query_id || "",
        query_name: searchFilterData.query_name || "",
        generic_research_id: searchFilterData.generic_research_id || "",
        generic_research_code: searchFilterData.generic_research_code || "",
        generic_execution_id: searchFilterData.generic_execution_id || "",
        generic_research_query_id:
          searchFilterData.generic_research_query_id || "", // ✅ ADDED: Sync generic_research_query_id field
        execution_status: searchFilterData.execution_status || "",
        comparison_research_query_id:
          searchFilterData.comparison_research_query_id || "", // ✅ ADDED: Sync comparison_research_query_id field
        comparison_research_query_available:
          searchFilterData.comparison_research_query_available ?? null, // ✅ ADDED: Sync comparison_research_query_available field (use ?? to preserve false)
        status_filter: searchFilterData.status_filter || "", // ✅ ADDED: Sync status_filter field
        query_type_filter: searchFilterData.query_type_filter || "", // ✅ ADDED: Sync query_type_filter field
        system_id: searchFilterData.system_id || "", // ✅ ADDED: Sync system_id field
        resource_type: searchFilterData.resource_type || "", // ✅ ADDED: Sync resource_type field
        run_name: searchFilterData.run_name || "", // ✅ ADDED: Sync run_name field
        domain_linkage_ids: searchFilterData.domain_linkage_ids || [], // ✅ ADDED: Sync domain_linkage_ids field
        source_domain_id: searchFilterData.source_domain_id || [], // ✅ UPDATED: Sync source_domain_id field as array
        target_domain_id: searchFilterData.target_domain_id || [], // ✅ UPDATED: Sync target_domain_id field as array
        source_domain_key: searchFilterData.source_domain_key || "", // ✅ ADDED: Sync source_domain_key field
        target_domain_key: searchFilterData.target_domain_key || "", // ✅ ADDED: Sync target_domain_key field
        username: searchFilterData.username || "", // ✅ ADDED: Sync username field
        email: searchFilterData.email || "", // ✅ ADDED: Sync email field
        role_name: searchFilterData.role_name || "", // ✅ ADDED: Sync role_name field
        group_name: searchFilterData.group_name || "", // ✅ ADDED: Sync group_name field
        tag_name: searchFilterData.tag_name || "", // ✅ ADDED: Sync tag_name field
        system_name: searchFilterData.system_name || "", // ✅ ADDED: Sync system_name field
        connection_runtime_id: searchFilterData.connection_runtime_id || "", // ✅ ADDED: Sync connection_runtime_id field
        code: searchFilterData.code || "", // ✅ ADDED: Sync code field
        connection_type: searchFilterData.connection_type || "", // ✅ ADDED: Sync connection_type field
        run_instance_id: searchFilterData.run_instance_id || "", // ✅ ADDED: Sync run_instance_id field
        name: searchFilterData.name || "", // ✅ ADDED: Sync name field (resource column name search)
        resource_column_filter_ids:
          searchFilterData.resource_column_filter_ids || "", // ✅ ADDED: Sync resource_column_filter_ids field
        issue_classification_id:
          searchFilterData.issue_classification_id ?? null,
        issue_reason_id: searchFilterData.issue_reason_id ?? null,
        severity: searchFilterData.severity || "", // ✅ ADDED: Sync severity field
        execution_sub_group_id: searchFilterData.execution_sub_group_id || "", // ✅ ADDED: Sync execution_sub_group_id field
      };
      setFilterData(newFilterData);
      // Also sync the domain ID for the AutoCompleteDomainList
      setCurrentDomainId(searchFilterData.domain_id || null);
      // Also sync the rule ID for the AutoCompleteRuleList
      setCurrentRuleId(searchFilterData.rule_id || null);
      // Also sync the resource ID for the AutoCompleteResourceList
      setCurrentResourceId(searchFilterData.resource_id || null);
      // Also sync the run ID for the AutoCompleteRunList
      setCurrentRunId(searchFilterData.run_id || null); // Sync run ID
      // Also sync the comparison research query ID for the AutoCompleteComparisonResearchQueryId
      setCurrentComparisonResearchQueryId(
        searchFilterData.comparison_research_query_id || null,
      );
      // Also sync the generic research query ID for the AutoCompleteGenericResearchQueryId
      setCurrentGenericResearchQueryId(
        searchFilterData.generic_research_query_id || null,
      );
      // Also sync the status filter for the AutocompleteStatusField
      setCurrentStatusFilter(searchFilterData.status_filter || null);
      // Also sync the execution sub group ID for the AutoCompleteExecutionSubGroup
      setCurrentExecutionSubGroupId(
        searchFilterData.execution_sub_group_id || null,
      );
    }
  }, [searchFilterData]);

  // Refs
  const filterSidebarRef = useRef<HTMLDivElement>(null);

  // Additional state for users and domain
  const [allUsersList, setAllUsersList] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentDomainId, setCurrentDomainId] = useState<any>(Number() || null);
  const [currentRuleId, setCurrentRuleId] = useState<any>(Number() || null);
  const [currentResourceId, setCurrentResourceId] = useState<any>(
    Number() || null,
  );
  const [currentRunId, setCurrentRunId] = useState<any>(Number() || null);
  const [
    currentComparisonResearchQueryId,
    setCurrentComparisonResearchQueryId,
  ] = useState<any>(Number() || null);
  const [currentGenericResearchQueryId, setCurrentGenericResearchQueryId] =
    useState<any>(Number() || null);
  const [currentStatusFilter, setCurrentStatusFilter] = useState<any>(null);
  const [currentSystemId, setCurrentSystemId] = useState<any>(Number() || null);
  const [currentExecutionSubGroupId, setCurrentExecutionSubGroupId] =
    useState<any>(Number() || null);
  const [isSystemLoading, setIsSystemLoading] = useState<boolean>(false);

  // Fetch users list
  const [usersList] = useFetchUsersList({
    setIsLoading,
  });

  // Get system context and fetch system data
  const { allSystemData, setAllSystemData } = useSystemContext();
  const [systemData] = useFetchAllSystem({
    setIsLoading: setIsSystemLoading,
  });

  // Update context state when system data is fetched
  useEffect(() => {
    if (systemData && Array.isArray(systemData)) {
      setAllSystemData(systemData);
    }
  }, [systemData, setAllSystemData]);

  useEffect(() => {
    setAllUsersList(usersList);
  }, [usersList]);

  // Computed values for required fields
  const isEndDateRequired = requiredFields?.includes("end_date");
  const isStartDateRequired = requiredFields?.includes("start_date");
  const isAssignedUserRequired = requiredFields?.includes("assigned_user");
  const isAssignedByRequired = requiredFields?.includes("assigned_by");
  const isDomainListRequired = requiredFields?.includes("domain_list");
  const isDomainNameRequired = requiredFields?.includes("domain_name");
  const isResourceListRequired = requiredFields?.includes("resource_list");
  const isRuleRequired = requiredFields?.includes("rule");
  const isRuleListRequired = requiredFields?.includes("rule_list");
  const isRunListRequired = requiredFields?.includes("run_list");
  const isExecutionIdRequired = requiredFields?.includes("execution_id");
  const isRuleIdRequired = requiredFields?.includes("rule_id");
  const isRuleNameRequired = requiredFields?.includes("rule_name");
  const isStatusRequired = requiredFields?.includes("status");
  const isClassificationRequired = requiredFields?.includes("classification");
  const isReasonRequired = requiredFields?.includes("reason");
  const isGenericResearchIdRequired = requiredFields?.includes(
    "generic_research_id",
  );
  const isGenericResearchCodeRequired = requiredFields?.includes(
    "generic_research_code",
  );
  const isGenericExecutionIdRequired = requiredFields?.includes(
    "generic_execution_id",
  );
  const isGenericResearchQueryIdRequired = requiredFields?.includes(
    "generic_research_query_id",
  );
  const isExecutionStatusRequired =
    requiredFields?.includes("execution_status");
  const isQueryNameRequired = requiredFields?.includes("query_name");
  const isNameRequired = requiredFields?.includes("name"); // NEW: For resource column name search
  const isResourceColumnFilterIdsRequired = requiredFields?.includes(
    "resource_column_filter_ids",
  ); // NEW: For resource column filter IDs
  const isComparisonResearchQueryIdRequired = requiredFields?.includes(
    "comparison_research_query_id",
  );
  const isComparisonResearchQueryAvailableRequired = requiredFields?.includes(
    "comparison_research_query_available",
  );
  const isStatusFilterRequired = requiredFields?.includes("status_filter");
  const isQueryTypeFilterRequired =
    requiredFields?.includes("query_type_filter");
  const isSystemIdRequired = requiredFields?.includes("system_id");
  const isResourceTypeRequired = requiredFields?.includes("resource_type");
  const isRunNameRequired = requiredFields?.includes("run_name");
  const isDomainLinkageIdsRequired =
    requiredFields?.includes("domain_linkage_ids");
  const isSourceDomainIdRequired = requiredFields?.includes("source_domain_id");
  const isTargetDomainIdRequired = requiredFields?.includes("target_domain_id");
  const isSourceDomainKeyRequired =
    requiredFields?.includes("source_domain_key");
  const isTargetDomainKeyRequired =
    requiredFields?.includes("target_domain_key");
  const isUsernameRequired = requiredFields?.includes("username");
  const isEmailRequired = requiredFields?.includes("email");
  const isRoleNameRequired = requiredFields?.includes("role_name");
  const isGroupNameRequired = requiredFields?.includes("group_name");
  const isTagNameRequired = requiredFields?.includes("tag_name");
  const isSystemNameRequired = requiredFields?.includes("system_name");
  const isConnectionRuntimeIdRequired = requiredFields?.includes(
    "connection_runtime_id",
  );
  const isCodeRequired = requiredFields?.includes("code");
  const isConnectionTypeRequired = requiredFields?.includes("connection_type");
  const isRunInstanceIdRequired = requiredFields?.includes("run_instance_id");
  const isIssueClassificationNameRequired = requiredFields?.includes(
    "issue_classification_name",
  );
  const isReasonNameRequired = requiredFields?.includes("reason_name");
  const isSeverityRequired = requiredFields?.includes("severity"); // ✅ ADDED
  const isExecutionSubGroupListRequired = requiredFields?.includes(
    "execution_sub_group_list",
  ); // ✅ ADDED

  // Get fields that should be in the "More" dropdown
  const moreFields = requiredFields.filter(
    (field) => !primaryFields.includes(field),
  );

  // Check if any "More" fields have values
  const hasMoreFieldValues = moreFields.some((field) => {
    const fieldKey =
      field === "domain_list"
        ? "domain_id"
        : field === "resource_list"
          ? "resource_id"
          : field === "rule_list"
            ? "rule_id"
            : field === "run_list"
              ? "run_id"
              : field === "rule"
                ? currentPageType === "execution"
                  ? "rule_id"
                  : "resource_id"
                : field === "generic_research_id"
                  ? "research_query_id"
                  : field === "generic_research_query_id"
                    ? "generic_research_query_id"
                    : field === "comparison_research_query_id"
                      ? "comparison_research_query_id"
                      : field === "comparison_research_query_available"
                        ? "comparison_research_query_available"
                        : field === "status_filter"
                          ? "status_filter"
                          : field;
    return filterData[fieldKey] && filterData[fieldKey] !== "";
  });

  // More menu handlers
  const handleMoreMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMoreMenuAnchorEl(event.currentTarget);
  };

  const handleMoreMenuClose = () => {
    setMoreMenuAnchorEl(null);
  };

  // Event handlers
  const handleDateChange = useCallback(
    (value: Dayjs | null, field: "start_date" | "end_date") => {
      const formattedDate = dayjs(value).isValid()
        ? dayjs(value).format("YYYY-MM-DD")
        : "";

      setFilterData((prevData) => ({
        ...prevData,
        [field]: formattedDate,
      }));
    },
    [],
  );

  const handleFilterUserChange = useCallback((newValue: any, name: string) => {
    setFilterData((prev) => ({
      ...prev,
      [name]: newValue?.username || "",
    }));
  }, []);

  const handleDomainChange = useCallback(
    (_e: any, value: any) => {
      setFilterData((prevData) => ({
        ...prevData,
        domain_id: value?.id || "",
        // ✅ Clear resource_id when domain changes if cascading is enabled
        ...(fieldRequirements?.enableDomainResourceCascading && {
          resource_id: "",
        }),
      }));
      setCurrentDomainId(value?.id || null);
      // ✅ Clear currentResourceId when domain changes if cascading is enabled
      if (fieldRequirements?.enableDomainResourceCascading) {
        setCurrentResourceId(null);
      }
    },
    [fieldRequirements?.enableDomainResourceCascading],
  );

  const handleRuleChange = useCallback((_e: any, value: any) => {
    // Handle multiple selection mode (value is an array)
    if (Array.isArray(value)) {
      const ruleIds = value.map((item: any) => item.id);
      setFilterData((prevData: any) => ({
        ...prevData,
        rule_id: ruleIds.length > 0 ? ruleIds : "",
      }));
      setCurrentRuleId(ruleIds.length > 0 ? ruleIds : null);
    } else {
      // Handle single selection mode (value is an object or null)
      setFilterData((prevData) => ({
        ...prevData,
        rule_id: value?.id || "",
      }));
      setCurrentRuleId(value?.id || null);
    }
  }, []);

  const handleRunChange = useCallback((_e: any, value: any) => {
    setFilterData((prevData) => ({
      ...prevData,
      run_id: value?.id || "", // ID for API filtering
      //run: value?.run_name || "", // Name for display
    }));
    setCurrentRunId(value?.id || null);
  }, []);

  const handleExecutionSubGroupChange = useCallback((_e: any, value: any) => {
    setFilterData((prevData) => ({
      ...prevData,
      execution_sub_group_id: value?.id || "",
    }));
    setCurrentExecutionSubGroupId(value?.id || null);
  }, []);

  const handleComparisonResearchQueryChange = useCallback(
    (_e: any, value: any) => {
      // Handle multiple selection mode (value is an array)
      if (Array.isArray(value)) {
        const queryIds = value.map((item: any) => item.id);
        setFilterData((prevData: any) => ({
          ...prevData,
          comparison_research_query_id: queryIds.length > 0 ? queryIds : "",
        }));
        setCurrentComparisonResearchQueryId(
          queryIds.length > 0 ? queryIds : null,
        );
      } else {
        // Handle single selection mode (value is an object or null)
        setFilterData((prevData) => ({
          ...prevData,
          comparison_research_query_id: value?.id || "",
        }));
        setCurrentComparisonResearchQueryId(value?.id || null);
      }
    },
    [],
  );

  const handleGenericResearchQueryChange = useCallback(
    (_e: any, value: any) => {
      setFilterData((prevData) => ({
        ...prevData,
        generic_research_query_id: value?.id || "",
      }));
      setCurrentGenericResearchQueryId(value?.id || null);
    },
    [],
  );

  const handleStatusFilterChange = useCallback((_e: any, value: any) => {
    setFilterData((prevData) => ({
      ...prevData,
      status_filter: value || "",
    }));
    setCurrentStatusFilter(value || null);
  }, []);

  const handleResourceChange = useCallback((_e: any, value: any) => {
    setFilterData((prevData) => ({
      ...prevData,
      resource_id: value?.id || "",
    }));
    setCurrentResourceId(value?.id || null);
  }, []);

  const handleFilterSearchChange = useCallback((e: any) => {
    const { name, value } = e.target;
    setFilterData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleFilterSearchIdChange = useCallback((e: any) => {
    const { name, value } = e.target;
    const trimmedValue = value.trim();
    // Only allow numeric input and basic controls like Backspace, Tab, Arrow keys
    if (/^\d*$/.test(trimmedValue)) {
      setFilterData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  }, []);

  // Clear reason when classification changes to prevent invalid combinations
  useEffect(() => {
    const currentReason = filterData?.reason;

    // If current reason is not in the dynamic reason options, clear it
    if (currentReason && !reasonOptions.includes(currentReason)) {
      setFilterData((prev) => ({
        ...prev,
        reason: "",
      }));
    }
  }, [filterData?.classification, reasonOptions, filterData?.reason]);

  const handleSubmitFilter = useCallback(() => {
    // Format data to match parent component expectations
    const formattedData = {
      ...searchFilterData,
      end_date: filterData.end_date || null,
      start_date: filterData.start_date || null,
      assigned_user: filterData.assigned_user || "",
      assigned_by: filterData.assigned_by || "",
      domain_id: filterData.domain_id || null,
      domain_name: filterData.domain_name || "", // ✅ ADDED: Include domain_name in formatted data
      rule_id: filterData.rule_id || null,
      resource_id: filterData.resource_id || null,
      execution_id: filterData.execution_id || null,
      rule_name: filterData.rule_name || "",
      run: filterData.run || "",
      run_id: filterData.run_id || null, // Include run_id in formatted data
      run_name: filterData.run_name || "", // ✅ ADDED: Include run_name in formatted data
      status: filterData.status || "",
      classification: filterData.classification || "",
      reason: filterData.reason || "",
      issue_classification_id: selectedClassificationId ?? null,
      issue_reason_id: selectedReasonId ?? null,
      research_query_id: filterData.research_query_id || "",
      query_name: filterData.query_name || "",
      generic_research_id: filterData.generic_research_id || "",
      generic_research_code: filterData.generic_research_code || "",
      generic_execution_id: filterData.generic_execution_id || "",
      generic_research_query_id: filterData.generic_research_query_id || "", // ✅ ADDED: Include generic_research_query_id in formatted data
      execution_status: filterData.execution_status || "",
      comparison_research_query_id:
        filterData.comparison_research_query_id || "", // ✅ ADDED: Include comparison_research_query_id in formatted data
      comparison_research_query_available:
        filterData.comparison_research_query_available ?? null, // ✅ ADDED: Include comparison_research_query_available in formatted data (use ?? to preserve false)
      status_filter: filterData.status_filter || "", // ✅ ADDED: Include status_filter in formatted data
      query_type_filter: filterData.query_type_filter || "", // ✅ ADDED: Include query_type_filter in formatted data
      system_id: filterData.system_id || null, // ✅ ADDED: Include system_id in formatted data
      resource_type: filterData.resource_type || "", // ✅ ADDED: Include resource_type in formatted data
      domain_linkage_ids: filterData.domain_linkage_ids || [], // ✅ ADDED: Include domain_linkage_ids in formatted data
      source_domain_id: filterData.source_domain_id || [], // ✅ ADDED: Include source_domain_id in formatted data
      target_domain_id: filterData.target_domain_id || [], // ✅ ADDED: Include target_domain_id in formatted data
      source_domain_key: filterData.source_domain_key || "", // ✅ ADDED: Include source_domain_key in formatted data
      target_domain_key: filterData.target_domain_key || "", // ✅ ADDED: Include target_domain_key in formatted data
      username: filterData.username || "", // ✅ ADDED: Include username in formatted data
      email: filterData.email || "", // ✅ ADDED: Include email in formatted data
      role_name: filterData.role_name || "", // ✅ ADDED: Include role_name in formatted data
      group_name: filterData.group_name || "", // ✅ ADDED: Include group_name in formatted data
      tag_name: filterData.tag_name || "", // ✅ ADDED: Include tag_name in formatted data
      system_name: filterData.system_name || "", // ✅ ADDED: Include system_name in formatted data
      connection_runtime_id: filterData.connection_runtime_id || "", // ✅ ADDED: Include connection_runtime_id in formatted data
      code: filterData.code || "", // ✅ ADDED: Include code in formatted data
      connection_type: filterData.connection_type || "", // ✅ ADDED: Include connection_type in formatted data
      name: filterData.name || "", // ✅ ADDED: Include name in formatted data (resource column name)
      resource_column_filter_ids: filterData.resource_column_filter_ids || "", // ✅ ADDED: Include resource_column_filter_ids in formatted data
      issue_classification_name: filterData.issue_classification_name || "", // ✅ ADDED: Include issue_classification_name in formatted data
      reason_name: filterData.reason_name || "", // ✅ ADDED: Include reason_name in formatted data
      severity: filterData.severity || "", // ✅ ADDED: Include severity in formatted data
      execution_sub_group_id: filterData.execution_sub_group_id || null, // ✅ ADDED: Include execution_sub_group_id in formatted data
    };
    setSearchFilterData(formattedData);
  }, [filterData, searchFilterData, setSearchFilterData]);

  const handleClearFilter = useCallback(() => {
    // Get default status filter value from fieldRequirements (if provided)
    const defaultStatusFilterValue =
      fieldRequirements?.defaultStatusFilter || "";

    setFilterData({
      ...INITIAL_FILTER_DATA,
      status_filter: defaultStatusFilterValue, // Reset to default status filter value
    });
    setCurrentDomainId(null);
    setCurrentRuleId(null);
    setCurrentResourceId(null);
    setCurrentRunId(null);
    setCurrentComparisonResearchQueryId(null); // ✅ ADDED: Clear comparison research query ID state
    setCurrentGenericResearchQueryId(null); // ✅ ADDED: Clear generic research query ID state
    setCurrentStatusFilter(defaultStatusFilterValue || null); // ✅ Reset to default status filter value
    setCurrentSystemId(null); // ✅ ADDED: Clear system ID state
    setCurrentExecutionSubGroupId(null); // ✅ ADDED: Clear execution sub group ID state
    // Reset to the initial search filter structure expected by parent
    const clearedData = {
      ...searchFilterData,
      start_date: null,
      end_date: null,
      assigned_user: "",
      assigned_by: "",
      domain_id: null,
      domain_name: "", // ✅ ADDED: Clear domain_name field
      rule_id: null,
      resource_id: null,
      execution_id: null,
      rule_name: "",
      run_id: null, // ✅ ADDED: Clear run_id field
      status: "",
      classification: "",
      reason: "",
      issue_classification_id: null,
      issue_reason_id: null,
      research_query_id: "",
      query_name: "",
      generic_research_code: "",
      generic_execution_id: "",
      generic_research_query_id: "",
      execution_status: "",
      comparison_research_query_id: "", // ✅ ADDED: Clear comparison_research_query_id field
      comparison_research_query_available: null, // ✅ ADDED: Clear comparison_research_query_available field
      status_filter: defaultStatusFilterValue, // ✅ Reset to default status filter value
      query_type_filter: "", // ✅ ADDED: Clear query_type_filter field
      system_id: null, // ✅ ADDED: Clear system_id field
      run_name: "", // ✅ ADDED: Clear run_name field
      resource_type: "", // ✅ ADDED: Clear resource_type field
      domain_linkage_ids: [], // ✅ ADDED: Clear domain_linkage_ids field
      source_domain_id: [], // ✅ ADDED: Clear source_domain_id field
      target_domain_id: [], // ✅ ADDED: Clear target_domain_id field
      source_domain_key: "", // ✅ ADDED: Clear source_domain_key field
      target_domain_key: "", // ✅ ADDED: Clear target_domain_key field
      username: "", // ✅ ADDED: Clear username field
      email: "", // ✅ ADDED: Clear email field
      role_name: "", // ✅ ADDED: Clear role_name field
      group_name: "", // ✅ ADDED: Clear group_name field
      tag_name: "", // ✅ ADDED: Clear tag_name field
      system_name: "", // ✅ ADDED: Clear system_name field
      connection_runtime_id: "", // ✅ ADDED: Clear connection_runtime_id field
      code: "", // ✅ ADDED: Clear code field
      connection_type: "", // ✅ ADDED: Clear connection_type field
      name: "", // ✅ ADDED: Clear name field (resource column name)
      resource_column_filter_ids: "", // ✅ ADDED: Clear resource_column_filter_ids field
      issue_classification_name: "", // ✅ ADDED: Clear issue_classification_name field
      reason_name: "", // ✅ ADDED: Clear reason_name field
      severity: "", // ✅ ADDED: Clear severity field
      execution_sub_group_id: null, // ✅ ADDED: Clear execution_sub_group_id field
    };
    setSearchFilterData(clearedData);
  }, [
    searchFilterData,
    setSearchFilterData,
    fieldRequirements?.defaultStatusFilter,
  ]);

  // Render helpers for primary fields
  const renderStartDateField = (isDropdown = false) => {
    if (!isStartDateRequired) return null;

    // Show label only if "start_date" is NOT in primaryFields
    const showLabel = !primaryFields.includes("start_date");

    const content = (
      <div
        className="filter-field-group"
        style={isDropdown ? { marginBottom: "16px" } : {}}
      >
        <div className="filter-field">
          {showLabel && (
            <div className="filter-label">
              <span className="position-relative">
                {fieldRequirements.dateTitle || t("common.labels.startDate")}
                {fieldRequirements.tooltipRequired && (
                  <Tooltip
                    title={t("search.searchResultsDateRange")}
                    placement="top"
                    arrow
                  >
                    <span className="upload-icon-info">
                      <InfoIcon sx={{ fontSize: 16 }} />
                    </span>
                  </Tooltip>
                )}
              </span>
            </div>
          )}
          <div className="filter-input">
            <Box className="datepicker-container-extra-icon">
              <DatePicker
                value={
                  filterData.start_date ? dayjs(filterData.start_date) : null
                }
                onChange={(value) => handleDateChange(value, "start_date")}
                minDate={dayjs().subtract(6, "month")}
                maxDate={dayjs()}
                format="YYYY-MM-DD"
                className="form-control-autocomplete date-picker min-height-36"
                slotProps={{
                  textField: {
                    placeholder:
                      fieldRequirements.dateTitle ||
                      t("common.labels.startDate"),
                  },
                  actionBar: { actions: ["today"] },
                  layout: {
                    sx: {
                      [`.${pickersLayoutClasses.actionBar}`]: {
                        gridColumn: 2,
                        color: "#000000",
                      },
                    },
                  },
                }}
              />
            </Box>
          </div>
        </div>
      </div>
    );
    return isDropdown ? (
      content
    ) : (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    );
  };

  const renderEndDateField = (isDropdown = false) => {
    if (!isEndDateRequired) return null;

    // Show label only if "end_date" is NOT in primaryFields
    const showLabel = !primaryFields.includes("end_date");

    const content = (
      <div
        className="filter-field-group"
        style={isDropdown ? { marginBottom: "16px" } : {}}
      >
        <div className="filter-field">
          {showLabel && (
            <div className="filter-label">
              <span className="position-relative">End Date</span>
            </div>
          )}
          <div className="filter-input">
            <Box className="datepicker-container-extra-icon">
              <DatePicker
                value={filterData.end_date ? dayjs(filterData.end_date) : null}
                onChange={(value) => handleDateChange(value, "end_date")}
                minDate={dayjs().subtract(6, "month")}
                maxDate={dayjs()}
                format="YYYY-MM-DD"
                className="form-control-autocomplete date-picker min-height-36"
                slotProps={{
                  textField: {
                    placeholder: t("common.labels.endDate"),
                  },
                  actionBar: { actions: ["today"] },
                  layout: {
                    sx: {
                      [`.${pickersLayoutClasses.actionBar}`]: {
                        gridColumn: 2,
                        color: "#000000",
                      },
                    },
                  },
                }}
              />
            </Box>
          </div>
        </div>
      </div>
    );
    return isDropdown ? (
      content
    ) : (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    );
  };

  const renderClassificationField = (isPrimary = false) => {
    if (!isClassificationRequired) return null;

    // Show label only if "classification" is NOT in primaryFields
    const showLabel = !primaryFields.includes("classification");

    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">Classification</div>}
          <div className="filter-input">
            <Autocomplete
              className="form-control-autocomplete dropdown-padding-1"
              options={classificationOptions}
              value={filterData?.classification || null}
              onChange={(_event, newValue) => {
                setFilterData((prev) => ({
                  ...prev,
                  classification: newValue || "",
                  reason: "",
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className="form-control"
                  name="classification"
                  placeholder="Select classification"
                />
              )}
            />
          </div>
        </div>
      </div>
    );

    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  const renderReasonField = (isPrimary = false) => {
    if (!isReasonRequired) return null;

    // Show label only if "reason" is NOT in primaryFields
    const showLabel = !primaryFields.includes("reason");

    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">Reason</div>}
          <div className="filter-input">
            <Autocomplete
              className="form-control-autocomplete dropdown-padding-1"
              options={reasonOptions}
              value={filterData?.reason || null}
              onChange={(_event, newValue) => {
                handleFilterSearchChange({
                  target: {
                    name: "reason",
                    value: newValue || "",
                  },
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className="form-control"
                  name="reason"
                  placeholder="Select reason"
                />
              )}
            />
          </div>
        </div>
      </div>
    );

    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  const renderDomainListField = (isPrimary = false) => {
    if (!isDomainListRequired) return null;

    // Show label only if "domain_list" is NOT in primaryFields
    const showLabel = !primaryFields.includes("domain_list");

    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">Domain</div>}

          <div className="filter-input">
            <AutoCompleteDomainList
              setIsLoading={setIsLoading}
              handelOnChangeDomain={handleDomainChange}
              currentDomainId={currentDomainId}
              className="form-control-autocomplete autocomplete-no-label dropdown-padding-1"
              required={false}
              placeholder="Select domain"
              includeDomainId={fieldRequirements?.includeDomainId}
              lazyLoad={true}
            />
          </div>
        </div>
      </div>
    );

    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  const renderResourceListField = (isPrimary = false) => {
    if (!isResourceListRequired) return null;

    // Show label only if "resource_list" is NOT in primaryFields
    const showLabel = !primaryFields.includes("resource_list");

    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">Resource</div>}

          <div className="filter-input">
            <AutoCompleteResourceList
              setIsLoading={setIsLoading}
              handelOnChangeResource={handleResourceChange}
              currentResourceId={currentResourceId}
              className="form-control-autocomplete autocomplete-no-label dropdown-padding-1"
              required={false}
              placeholder="Select resource"
              includeResourceId={fieldRequirements?.includeResourceId}
              currentDomainId={
                fieldRequirements?.enableDomainResourceCascading
                  ? currentDomainId
                  : undefined
              }
              lazyLoad={true}
            />
          </div>
        </div>
      </div>
    );

    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  const renderRuleListField = (isPrimary = false) => {
    if (!isRuleListRequired) return null;

    // Show label only if "rule_list" is NOT in primaryFields
    const showLabel = !primaryFields.includes("rule_list");

    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">Rule</div>}

          <div className="filter-input">
            <AutoCompleteRuleList
              setIsLoading={setIsLoading}
              handelOnChangeRule={handleRuleChange}
              currentRuleId={currentRuleId}
              className="form-control-autocomplete autocomplete-no-label dropdown-padding-1"
              required={false}
              placeholder="Select rule"
              includeRuleId={fieldRequirements?.includeRuleId}
              isMultiple={fieldRequirements?.hasMultipleRuleIds}
              lazyLoad={true}
            />
          </div>
        </div>
      </div>
    );

    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  const renderRunListField = (isPrimary = false) => {
    if (!isRunListRequired) return null;

    // Show label only if "run_list" is NOT in primaryFields
    const showLabel = !primaryFields.includes("run_list");

    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">Execution Group</div>}

          <div className="filter-input">
            <AutoCompleteRunList
              setIsLoading={setIsLoading}
              handelOnChangeRun={handleRunChange}
              currentRunId={currentRunId}
              className="form-control-autocomplete autocomplete-no-label dropdown-padding-1"
              required={false}
              placeholder="Select execution group"
              includeRunId={fieldRequirements?.includeRunId}
              lazyLoad={true}
            />
          </div>
        </div>
      </div>
    );

    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  const renderExecutionSubGroupListField = (isPrimary = false) => {
    if (!isExecutionSubGroupListRequired) return null;

    // Show label only if "execution_sub_group_list" is NOT in primaryFields
    const showLabel = !primaryFields.includes("execution_sub_group_list");

    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">Execution Sub Group</div>}

          <div className="filter-input">
            <AutoCompleteExecutionSubGroup
              setIsLoading={setIsLoading}
              handelOnChangeExecutionSubGroup={handleExecutionSubGroupChange}
              currentExecutionSubGroupId={currentExecutionSubGroupId}
              className="form-control-autocomplete autocomplete-no-label dropdown-padding-1"
              required={false}
              placeholder="Select execution sub group"
              lazyLoad={true}
            />
          </div>
        </div>
      </div>
    );

    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  const renderComparisonResearchQueryIdField = (isPrimary = false) => {
    if (!isComparisonResearchQueryIdRequired) return null;

    // Show label only if "comparison_research_query_id" is NOT in primaryFields
    const showLabel = !primaryFields.includes("comparison_research_query_id");

    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && (
            <div className="filter-label">Comparison Research Query</div>
          )}

          <div className="filter-input">
            <AutoCompleteComparisonResearchQueryId
              setIsLoading={setIsLoading}
              handelOnChangeComparisonResearchQuery={
                handleComparisonResearchQueryChange
              }
              currentComparisonResearchQueryId={
                currentComparisonResearchQueryId
              }
              className="form-control-autocomplete autocomplete-no-label dropdown-padding-1"
              required={false}
              placeholder="Select comparison research query"
              includeComparisonResearchQueryId={
                fieldRequirements?.includeComparisonResearchQueryId
              }
              isMultiple={
                fieldRequirements?.hasMultipleComparisonResearchQueryIds
              }
              lazyLoad={true}
            />
          </div>
        </div>
      </div>
    );

    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  const renderComparisonResearchQueryAvailableField = (isPrimary = false) => {
    if (!isComparisonResearchQueryAvailableRequired) return null;

    // Show label only if "comparison_research_query_available" is NOT in primaryFields
    const showLabel = !primaryFields.includes(
      "comparison_research_query_available",
    );

    const options = [
      { label: t("common.status.all"), value: null },
      { label: "True", value: true },
      { label: "False", value: false },
    ];

    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">Has Research Query</div>}
          <div className="filter-input">
            <Autocomplete
              options={options}
              getOptionLabel={(option) => option.label}
              value={
                options.find(
                  (opt) =>
                    opt.value ===
                    filterData?.comparison_research_query_available,
                ) || options[0]
              }
              onChange={(event, newValue) => {
                setFilterData((prev) => ({
                  ...prev,
                  comparison_research_query_available: newValue?.value ?? null,
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={showLabel ? "" : "Has research query"}
                  className="form-control"
                />
              )}
              className="autocomplete-no-label"
            />
          </div>
        </div>
      </div>
    );

    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  const renderStatusFilterField = (isPrimary = false) => {
    if (!isStatusFilterRequired) return null;

    // Show label only if "status_filter" is NOT in primaryFields
    const showLabel = !primaryFields.includes("status_filter");

    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">Status</div>}

          <div className="filter-input">
            <AutocompleteStatusField
              setIsLoading={setIsLoading}
              handelOnChangeStatus={handleStatusFilterChange}
              currentStatus={currentStatusFilter}
              className="form-control-autocomplete autocomplete-no-label dropdown-padding-1"
              required={false}
              placeholder="Select status"
            />
          </div>
        </div>
      </div>
    );

    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  const renderQueryTypeFilterField = (isPrimary = false) => {
    if (!isQueryTypeFilterRequired) return null;

    // Show label only if "query_type_filter" is NOT in primaryFields
    const showLabel = !primaryFields.includes("query_type_filter");

    const queryTypeOptions = ["Resource", "External", "Mixed"];

    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">Query Type</div>}
          <div className="filter-input">
            <Autocomplete
              options={queryTypeOptions}
              value={filterData?.query_type_filter || null}
              onChange={(event, newValue) => {
                setFilterData((prev) => ({
                  ...prev,
                  query_type_filter: newValue || "",
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={showLabel ? "" : "Select query type"}
                  className="form-control"
                />
              )}
              className="autocomplete-no-label"
            />
          </div>
        </div>
      </div>
    );

    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  const renderSystemIdField = (isPrimary = false) => {
    if (!isSystemIdRequired) return null;

    // Show label only if "system_id" is NOT in primaryFields
    const showLabel = !primaryFields.includes("system_id");

    const handleSystemChange = (_e: any, value: any) => {
      setFilterData((prevData) => ({
        ...prevData,
        system_id: value?.id || "",
      }));
      setCurrentSystemId(value?.id || null);
    };

    const selectedSystem =
      allSystemData?.find(
        (system: any) => system.id === filterData?.system_id,
      ) || null;

    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">System</div>}

          <div className="filter-input">
            <SystemFilterDropdown
              allSystemData={allSystemData || []}
              isSystemDataLoading={isSystemLoading}
              selectedSystemId={filterData?.system_id || null}
              onSystemChange={handleSystemChange}
              className="form-control-autocomplete autocomplete-no-label dropdown-padding"
              placeholder="Select system"
              label=""
              includeSystemId={fieldRequirements?.includeSystemId}
            />
          </div>
        </div>
      </div>
    );

    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  const renderResourceTypeField = (isPrimary = false) => {
    if (!isResourceTypeRequired) return null;

    // Show label only if "resource_type" is NOT in primaryFields
    const showLabel = !primaryFields.includes("resource_type");

    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">Resource Type</div>}
          <div className="filter-input">
            <TextField
              type="text"
              name="resource_type"
              placeholder={showLabel ? "" : "Search by Resource Type"}
              value={filterData?.resource_type}
              onChange={handleFilterSearchChange}
              className="form-control"
            />
          </div>
        </div>
      </div>
    );

    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  const renderRunNameField = (isPrimary = false) => {
    if (!isRunNameRequired) return null;

    // Show label only if "run_name" is NOT in primaryFields
    const showLabel = !primaryFields.includes("run_name");

    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && (
            <div className="filter-label">Execution Group Name</div>
          )}
          <div className="filter-input">
            <TextField
              type="text"
              name="run_name"
              placeholder={showLabel ? "" : "Search by Execution Group Name"}
              value={filterData?.run_name}
              onChange={handleFilterSearchChange}
              className="form-control"
            />
          </div>
        </div>
      </div>
    );

    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  const renderSourceDomainKeyField = (isPrimary = false) => {
    if (!isSourceDomainKeyRequired) return null;
    const showLabel = !primaryFields.includes("source_domain_key");
    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">Source Domain Key</div>}
          <div className="filter-input">
            <TextField
              type="text"
              name="source_domain_key"
              placeholder={showLabel ? "" : "Search by Source Domain Key"}
              value={filterData?.source_domain_key}
              onChange={handleFilterSearchChange}
              className="form-control"
            />
          </div>
        </div>
      </div>
    );
    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  const renderTargetDomainKeyField = (isPrimary = false) => {
    if (!isTargetDomainKeyRequired) return null;
    const showLabel = !primaryFields.includes("target_domain_key");
    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">Target Domain Key</div>}
          <div className="filter-input">
            <TextField
              type="text"
              name="target_domain_key"
              placeholder={showLabel ? "" : "Search by Target Domain Key"}
              value={filterData?.target_domain_key}
              onChange={handleFilterSearchChange}
              className="form-control"
            />
          </div>
        </div>
      </div>
    );
    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  // Render Domain Linkage IDs field
  const renderDomainLinkageIdsField = (isPrimary = false) => {
    if (!isDomainLinkageIdsRequired) return null;
    const showLabel = !primaryFields.includes("domain_linkage_ids");

    const handleDomainLinkageIdsChange = (_e: any, value: any) => {
      setFilterData((prevData) => ({
        ...prevData,
        domain_linkage_ids: value || [],
      }));
    };

    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">Domain Linkage IDs</div>}
          <div className="filter-input">
            <AutoCompleteDomainList
              setIsLoading={setIsLoading}
              handelOnChangeDomain={handleDomainLinkageIdsChange}
              currentDomainId={filterData.domain_linkage_ids}
              className="form-control-autocomplete autocomplete-no-label dropdown-padding-1"
              required={false}
              placeholder="Select domain linkage IDs"
              includeDomainId={true}
              multiple={true}
              lazyLoad={true}
            />
          </div>
        </div>
      </div>
    );

    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  // Render Source Domain ID field
  const renderSourceDomainIdField = (isPrimary = false) => {
    if (!isSourceDomainIdRequired) return null;
    const showLabel = !primaryFields.includes("source_domain_id");

    const handleSourceDomainIdChange = (_e: any, value: any) => {
      setFilterData((prevData) => ({
        ...prevData,
        source_domain_id: value || [],
      }));
    };

    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">Source Domain</div>}
          <div className="filter-input">
            <AutoCompleteDomainList
              setIsLoading={setIsLoading}
              handelOnChangeDomain={handleSourceDomainIdChange}
              currentDomainId={filterData.source_domain_id}
              className="form-control-autocomplete autocomplete-no-label dropdown-padding-1"
              required={false}
              placeholder="Select source domain"
              includeDomainId={fieldRequirements?.includeDomainId}
              multiple={true}
              lazyLoad={true}
            />
          </div>
        </div>
      </div>
    );

    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  // Render Target Domain ID field
  const renderTargetDomainIdField = (isPrimary = false) => {
    if (!isTargetDomainIdRequired) return null;
    const showLabel = !primaryFields.includes("target_domain_id");

    const handleTargetDomainIdChange = (_e: any, value: any) => {
      setFilterData((prevData) => ({
        ...prevData,
        target_domain_id: value || [],
      }));
    };

    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">Target Domain</div>}
          <div className="filter-input">
            <AutoCompleteDomainList
              setIsLoading={setIsLoading}
              handelOnChangeDomain={handleTargetDomainIdChange}
              currentDomainId={filterData.target_domain_id}
              className="form-control-autocomplete autocomplete-no-label dropdown-padding-1"
              required={false}
              placeholder="Select target domain"
              includeDomainId={fieldRequirements?.includeDomainId}
              multiple={true}
              lazyLoad={true}
            />
          </div>
        </div>
      </div>
    );

    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  // Render helpers for "More" dropdown fields
  const renderAssignedByField = () => {
    if (!isAssignedByRequired) return null;

    // Show label only if "assigned_by" is NOT in primaryFields
    const showLabel = !primaryFields.includes("assigned_by");

    return (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">Assigned By</div>}
          <Box className="filter-input">
            <Autocomplete
              sx={{
                "& .MuiSelect-select": {
                  height: "36px",
                },
              }}
              className="form-control-autocomplete dropdown-padding-1"
              options={Array.isArray(allUsersList) ? allUsersList : []}
              getOptionLabel={(option) => option.username}
              onChange={(_e, newValue) =>
                handleFilterUserChange(newValue, "assigned_by")
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t("common.placeholder.select")}
                  variant="outlined"
                  name="assigned_by"
                />
              )}
              value={
                (allUsersList?.length > 0 &&
                  allUsersList?.find(
                    (user: { username: any }) =>
                      user.username === filterData?.assigned_by,
                  )) ||
                null
              }
              isOptionEqualToValue={(option, value) =>
                option.username === value.username
              }
              PopperComponent={(props) => (
                <Popper
                  sx={{
                    "& .MuiAutocomplete-listbox": {
                      "& .MuiAutocomplete-option": {
                        "&.Mui-focused[aria-selected='true']": {
                          backgroundColor: "rgba(0, 0, 0, 0.10)",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "rgba(0, 0, 0, 0.10)",
                        },
                      },
                    },
                  }}
                  {...props}
                />
              )}
            />
          </Box>
        </div>
      </div>
    );
  };

  const renderAssignedUserField = () => {
    if (!isAssignedUserRequired) return null;

    // Show label only if "assigned_user" is NOT in primaryFields
    const showLabel = !primaryFields.includes("assigned_user");

    return (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">Assignee</div>}
          <Box className="filter-input">
            <Autocomplete
              sx={{
                "& .MuiSelect-select": {
                  height: "36px",
                },
              }}
              className="form-control-autocomplete dropdown-padding-1"
              options={Array.isArray(allUsersList) ? allUsersList : []}
              getOptionLabel={(option) => option.username}
              onChange={(_e, newValue) =>
                handleFilterUserChange(newValue, "assigned_user")
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  name="assigned_user"
                  placeholder={t("common.placeholder.select")}
                />
              )}
              value={
                (allUsersList?.length > 0 &&
                  allUsersList?.find(
                    (user: { username: any }) =>
                      user.username === filterData?.assigned_user,
                  )) ||
                null
              }
              isOptionEqualToValue={(option, value) =>
                option.username === value.username
              }
              PopperComponent={(props) => (
                <Popper
                  sx={{
                    "& .MuiAutocomplete-listbox": {
                      "& .MuiAutocomplete-option": {
                        "&.Mui-focused[aria-selected='true']": {
                          backgroundColor: "rgba(0, 0, 0, 0.10)",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "rgba(0, 0, 0, 0.10)",
                        },
                      },
                    },
                  }}
                  {...props}
                />
              )}
            />
          </Box>
        </div>
      </div>
    );
  };

  const renderRuleIdField = () => {
    if (!isRuleIdRequired) return null;

    // Show label only if "rule_id" is NOT in primaryFields
    const showLabel = !primaryFields.includes("rule_id");

    return (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">Rule ID</div>}
          <div className="filter-input">
            <TextField
              type="text"
              name="rule_id"
              value={filterData?.rule_id}
              onChange={handleFilterSearchIdChange}
              className="form-control"
            />
          </div>
        </div>
      </div>
    );
  };

  const renderRuleNameField = () => {
    if (!isRuleNameRequired) return null;

    // Show label only if "rule_name" is NOT in primaryFields
    const showLabel = !primaryFields.includes("rule_name");

    return (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">Rule Name</div>}
          <div className="filter-input">
            <TextField
              type="text"
              name="rule_name"
              value={filterData?.rule_name}
              onChange={handleFilterSearchChange}
              className="form-control"
            />
          </div>
        </div>
      </div>
    );
  };

  const renderDomainNameField = (isPrimary = false) => {
    if (!isDomainNameRequired) return null;

    // Show label only if "domain_name" is NOT in primaryFields
    const showLabel = !primaryFields.includes("domain_name");

    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">Domain Name</div>}
          <div className="filter-input">
            <TextField
              type="text"
              name="domain_name"
              placeholder={showLabel ? "" : "Search by Domain Name"}
              value={filterData?.domain_name}
              onChange={handleFilterSearchChange}
              className="form-control"
            />
          </div>
        </div>
      </div>
    );

    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  const renderStatusField = (isPrimary = false) => {
    if (!isStatusRequired) return null;

    // Show label only if "status" is NOT in primaryFields
    const showLabel = !primaryFields.includes("status");

    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">Status</div>}
          <div className="filter-input">
            <Autocomplete
              className="form-control-autocomplete dropdown-padding-1"
              options={issueStatus}
              value={filterData?.status || null}
              onChange={(_event, newValue) => {
                handleFilterSearchChange({
                  target: {
                    name: "status",
                    value: newValue || "",
                  },
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className="form-control"
                  name="status"
                  placeholder={
                    showLabel
                      ? t("common.placeholder.select")
                      : t("common.placeholder.selectStatus")
                  }
                />
              )}
            />
          </div>
        </div>
      </div>
    );

    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  const renderRuleField = () => {
    if (!isRuleRequired) return null;

    // Show label only if "rule" is NOT in primaryFields
    const showLabel = !primaryFields.includes("rule");

    return (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && (
            <div className="filter-label">
              {currentPageType === "execution" ? "Rule ID" : "Resource ID"}
            </div>
          )}
          <div className="filter-input">
            <TextField
              type="text"
              name={currentPageType === "execution" ? "rule_id" : "resource_id"}
              value={
                currentPageType === "execution"
                  ? filterData?.rule_id
                  : filterData?.resource_id
              }
              onChange={handleFilterSearchIdChange}
              className="form-control"
            />
          </div>
        </div>
      </div>
    );
  };

  const renderExecutionIdField = () => {
    if (!isExecutionIdRequired) return null;

    // Show label only if "execution_id" is NOT in primaryFields
    const showLabel = !primaryFields.includes("execution_id");

    return (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">Execution ID</div>}
          <div className="filter-input">
            <TextField
              type="text"
              name="execution_id"
              value={filterData?.execution_id}
              onChange={handleFilterSearchIdChange}
              className="form-control"
            />
          </div>
        </div>
      </div>
    );
  };

  const renderGenericResearchIdField = (isPrimary = false) => {
    if (!isGenericResearchIdRequired) return null;

    // Show label only if "generic_research_id" is NOT in primaryFields
    const showLabel = !primaryFields.includes("generic_research_id");

    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">Generic Research ID</div>}
          <div className="filter-input">
            <TextField
              type="text"
              name="generic_research_id"
              placeholder={
                showLabel
                  ? "Ex:1,2,3"
                  : fieldRequirements.placeholder || "Ex:1,2,3"
              }
              value={filterData?.generic_research_id || ""}
              onChange={(e) => {
                handleFilterSearchChange(e);
              }}
              className="form-control input-ellipsis"
            />
          </div>
        </div>
      </div>
    );

    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  const renderQueryNameField = (isPrimary = false) => {
    if (!isQueryNameRequired) return null;

    // Show label only if "query_name" is NOT in primaryFields
    const showLabel = !primaryFields.includes("query_name");

    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">Query Name</div>}
          <div className="filter-input">
            <TextField
              type="text"
              name="query_name"
              placeholder={
                showLabel
                  ? ""
                  : fieldRequirements.queryNamePlaceholder ||
                    "Resource column name"
              }
              value={filterData?.query_name}
              onChange={handleFilterSearchChange}
              className="form-control"
            />
          </div>
        </div>
      </div>
    );

    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  // ✅ NEW: Render function for name field (resource column name search)
  const renderNameField = (isPrimary = false) => {
    if (!isNameRequired) return null;

    // Show label only if "name" is NOT in primaryFields
    const showLabel = !primaryFields.includes("name");

    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">Name</div>}
          <div className="filter-input">
            <TextField
              type="text"
              name="name"
              placeholder={
                showLabel
                  ? ""
                  : fieldRequirements?.namePlaceholder || "Resource column name"
              }
              value={filterData?.name}
              onChange={handleFilterSearchChange}
              className="form-control"
            />
          </div>
        </div>
      </div>
    );

    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  // ✅ NEW: Render function for resource_column_filter_ids field
  const renderResourceColumnFilterIdsField = (isPrimary = false) => {
    if (!isResourceColumnFilterIdsRequired) return null;

    // Show label only if "resource_column_filter_ids" is NOT in primaryFields
    const showLabel = !primaryFields.includes("resource_column_filter_ids");

    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && (
            <div className="filter-label">Resource Column Filter IDs</div>
          )}
          <div className="filter-input">
            <TextField
              type="text"
              name="resource_column_filter_ids"
              placeholder={
                showLabel ? "" : "Comma-separated filter IDs (e.g., 1,2,3)"
              }
              value={filterData?.resource_column_filter_ids}
              onChange={handleFilterSearchChange}
              className="form-control"
            />
          </div>
        </div>
      </div>
    );

    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  const renderGenericResearchQueryIdField = (isPrimary = false) => {
    if (!isGenericResearchQueryIdRequired) return null;

    // Show label only if "generic_research_query_id" is NOT in primaryFields
    const showLabel = !primaryFields.includes("generic_research_query_id");

    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && (
            <div className="filter-label">Generic Research Query</div>
          )}

          <div className="filter-input">
            <AutoCompleteGenericResearchQueryId
              setIsLoading={setIsLoading}
              handelOnChangeGenericResearchQuery={
                handleGenericResearchQueryChange
              }
              currentGenericResearchQueryId={currentGenericResearchQueryId}
              className="form-control-autocomplete autocomplete-no-label dropdown-padding-1"
              required={false}
              placeholder="Select generic research query"
              includeGenericResearchQueryId={true}
              lazyLoad={true}
            />
          </div>
        </div>
      </div>
    );

    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  const renderGenericResearchCodeField = (isPrimary = false) => {
    if (!isGenericResearchCodeRequired) return null;

    // Show label only if "generic_research_code" is NOT in primaryFields
    const showLabel = !primaryFields.includes("generic_research_code");

    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && (
            <div className="filter-label">Generic Research Code</div>
          )}
          <div className="filter-input">
            <TextField
              type="text"
              name="generic_research_code"
              placeholder={showLabel ? "Enter code" : "Generic research code"}
              value={filterData?.generic_research_code || ""}
              onChange={(e) => {
                handleFilterSearchChange(e);
              }}
              className="form-control input-ellipsis"
            />
          </div>
        </div>
      </div>
    );

    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  const renderGenericExecutionIdField = (isPrimary = false) => {
    if (!isGenericExecutionIdRequired) return null;

    // Show label only if "generic_execution_id" is NOT in primaryFields
    const showLabel = !primaryFields.includes("generic_execution_id");

    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && (
            <div className="filter-label">Generic Execution ID</div>
          )}
          <div className="filter-input">
            <TextField
              type="text"
              name="generic_execution_id"
              placeholder={showLabel ? "Enter ID" : "Generic execution ID"}
              value={filterData?.generic_execution_id || ""}
              onChange={(e) => {
                handleFilterSearchChange(e);
              }}
              className="form-control"
            />
          </div>
        </div>
      </div>
    );

    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  const renderExecutionStatusField = (isPrimary = false) => {
    if (!isExecutionStatusRequired) return null;

    // Show label only if "execution_status" is NOT in primaryFields
    const showLabel = !primaryFields.includes("execution_status");

    // Execution status options matching FilterSearch component
    const executionOptions = ["Completed", "In_progress"];

    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">Execution Status</div>}
          <div className="filter-input">
            <Autocomplete
              className="form-control-autocomplete dropdown-padding-1"
              options={executionOptions}
              value={filterData?.execution_status || null}
              onChange={(_, newValue) => {
                handleFilterSearchChange({
                  target: {
                    name: "execution_status",
                    value: newValue || "",
                  },
                });
              }}
              renderInput={(params) => (
                <TextField {...params} className="form-control" />
              )}
            />
          </div>
        </div>
      </div>
    );

    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  const renderUsernameField = (isPrimary = false) => {
    if (!isUsernameRequired) return null;
    const showLabel = !primaryFields.includes("username");
    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">Username</div>}
          <div className="filter-input">
            <TextField
              type="text"
              name="username"
              placeholder={showLabel ? "" : "Search by Username"}
              value={filterData?.username}
              onChange={handleFilterSearchChange}
              className="form-control"
            />
          </div>
        </div>
      </div>
    );
    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  const renderEmailField = (isPrimary = false) => {
    if (!isEmailRequired) return null;
    const showLabel = !primaryFields.includes("email");
    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">Email</div>}
          <div className="filter-input">
            <TextField
              type="text"
              name="email"
              placeholder={showLabel ? "" : "Search by Email"}
              value={filterData?.email}
              onChange={handleFilterSearchChange}
              className="form-control"
            />
          </div>
        </div>
      </div>
    );
    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  const renderRoleNameField = (isPrimary = false) => {
    if (!isRoleNameRequired) return null;
    const showLabel = !primaryFields.includes("role_name");
    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">Role Name</div>}
          <div className="filter-input">
            <TextField
              type="text"
              name="role_name"
              placeholder={showLabel ? "" : "Search by Role Name"}
              value={filterData?.role_name}
              onChange={handleFilterSearchChange}
              className="form-control"
            />
          </div>
        </div>
      </div>
    );
    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  const renderGroupNameField = (isPrimary = false) => {
    if (!isGroupNameRequired) return null;
    const showLabel = !primaryFields.includes("group_name");
    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">Group Name</div>}
          <div className="filter-input">
            <TextField
              type="text"
              name="group_name"
              placeholder={showLabel ? "" : "Search by Group Name"}
              value={filterData?.group_name}
              onChange={handleFilterSearchChange}
              className="form-control"
            />
          </div>
        </div>
      </div>
    );
    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  const renderTagNameField = (isPrimary = false) => {
    if (!isTagNameRequired) return null;
    const showLabel = !primaryFields.includes("tag_name");
    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">Tag Name</div>}
          <div className="filter-input">
            <TextField
              type="text"
              name="tag_name"
              placeholder={showLabel ? "" : "Search by Tag Name"}
              value={filterData?.tag_name}
              onChange={handleFilterSearchChange}
              className="form-control"
            />
          </div>
        </div>
      </div>
    );
    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  const renderSystemNameField = (isPrimary = false) => {
    if (!isSystemNameRequired) return null;
    const showLabel = !primaryFields.includes("system_name");
    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">System Name</div>}
          <div className="filter-input">
            <TextField
              type="text"
              name="system_name"
              placeholder={showLabel ? "" : "Search by System Name"}
              value={filterData?.system_name}
              onChange={handleFilterSearchChange}
              className="form-control"
            />
          </div>
        </div>
      </div>
    );
    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  const renderConnectionRuntimeIdField = (isPrimary = false) => {
    if (!isConnectionRuntimeIdRequired) return null;
    const showLabel = !primaryFields.includes("connection_runtime_id");
    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && (
            <div className="filter-label">Connection Runtime ID</div>
          )}
          <div className="filter-input">
            <TextField
              type="text"
              name="connection_runtime_id"
              placeholder={showLabel ? "" : "Search by ID"}
              value={filterData?.connection_runtime_id}
              onChange={handleFilterSearchChange}
              className="form-control"
            />
          </div>
        </div>
      </div>
    );
    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  const renderCodeField = (isPrimary = false) => {
    if (!isCodeRequired) return null;
    const showLabel = !primaryFields.includes("code");
    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">Code</div>}
          <div className="filter-input">
            <TextField
              type="text"
              name="code"
              placeholder={showLabel ? "" : "Search by Code"}
              value={filterData?.code}
              onChange={handleFilterSearchChange}
              className="form-control"
            />
          </div>
        </div>
      </div>
    );
    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  const renderConnectionTypeField = (isPrimary = false) => {
    if (!isConnectionTypeRequired) return null;
    const showLabel = !primaryFields.includes("connection_type");
    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">Connection Type</div>}
          <div className="filter-input">
            <TextField
              type="text"
              name="connection_type"
              placeholder={showLabel ? "" : "Search by Type"}
              value={filterData?.connection_type}
              onChange={handleFilterSearchChange}
              className="form-control"
            />
          </div>
        </div>
      </div>
    );
    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  const renderRunInstanceIdField = (isPrimary = false) => {
    if (!isRunInstanceIdRequired) return null;
    const showLabel = !primaryFields.includes("run_instance_id");
    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">Execution Group ID</div>}
          <div className="filter-input">
            <TextField
              type="text"
              name="run_instance_id"
              placeholder={showLabel ? "" : "Filter by execution group ID"}
              value={filterData?.run_instance_id}
              onChange={handleFilterSearchChange}
              className="form-control"
            />
          </div>
        </div>
      </div>
    );
    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  const renderIssueClassificationNameField = (isPrimary = false) => {
    if (!isIssueClassificationNameRequired) return null;
    const showLabel = !primaryFields.includes("issue_classification_name");
    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">Classification Name</div>}
          <div className="filter-input">
            <TextField
              type="text"
              name="issue_classification_name"
              placeholder={showLabel ? "" : "Search by Classification Name"}
              value={filterData?.issue_classification_name}
              onChange={handleFilterSearchChange}
              className="form-control"
            />
          </div>
        </div>
      </div>
    );
    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  const renderSeverityField = (isPrimary = false) => {
    if (!isSeverityRequired) return null;

    const showLabel = !primaryFields.includes("severity");

    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">Severity</div>}
          <div className="filter-input">
            <Autocomplete
              options={scoringSeverityOptions}
              value={filterData?.severity || null}
              onChange={(_event, newValue) => {
                setFilterData((prev) => ({
                  ...prev,
                  severity: newValue || "",
                }));
              }}
              getOptionLabel={(opt) =>
                opt.charAt(0).toUpperCase() + opt.slice(1)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={showLabel ? "" : "Select severity"}
                  className="form-control"
                />
              )}
              className="form-control-autocomplete autocomplete-no-label dropdown-padding-1"
            />
          </div>
        </div>
      </div>
    );

    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  const renderReasonNameField = (isPrimary = false) => {
    if (!isReasonNameRequired) return null;
    const showLabel = !primaryFields.includes("reason_name");
    const content = (
      <div className="filter-field-group">
        <div className="filter-field">
          {showLabel && <div className="filter-label">Reason Name</div>}
          <div className="filter-input">
            <TextField
              type="text"
              name="reason_name"
              placeholder={showLabel ? "" : "Search by Reason Name"}
              value={filterData?.reason_name}
              onChange={handleFilterSearchChange}
              className="form-control"
            />
          </div>
        </div>
      </div>
    );
    return isPrimary ? (
      <Grid
        item
        xs={12}
        sm={4}
        md={largeWidth ? 4 : 3}
        lg={largeWidth ? 3 : 2}
        xl={largeWidth ? 3 : 2}
      >
        {content}
      </Grid>
    ) : (
      content
    );
  };

  // Dynamic function to render primary fields
  const renderPrimaryField = (fieldName: string): React.ReactElement | null => {
    switch (fieldName) {
      case "domain_list":
        return renderDomainListField(true);
      case "domain_name":
        return renderDomainNameField(true);
      case "resource_list":
        return renderResourceListField(true);
      case "rule_list":
        return renderRuleListField(true);
      case "run_list":
        return renderRunListField(true); // ✅ FIXED: was missing `true`, causing zero-width Grid
      case "execution_sub_group_list":
        return renderExecutionSubGroupListField(true);
      case "comparison_research_query_id":
        return renderComparisonResearchQueryIdField(true);
      case "status_filter":
        return renderStatusFilterField(true);
      case "classification":
        return renderClassificationField(true);
      case "reason":
        return renderReasonField(true);
      case "status":
        return renderStatusField(true);
      case "generic_research_id":
        return renderGenericResearchIdField(true);
      case "generic_research_code":
        return renderGenericResearchCodeField(true);
      case "generic_execution_id":
        return renderGenericExecutionIdField(true);
      case "execution_status":
        return renderExecutionStatusField(true);
      case "query_name":
        return renderQueryNameField(true);
      case "name": // ✅ NEW: Resource column name search
        return renderNameField(true);
      case "resource_column_filter_ids": // ✅ NEW: Resource column filter IDs
        return renderResourceColumnFilterIdsField(true);
      case "generic_research_query_id":
        return renderGenericResearchQueryIdField(true);
      case "query_type_filter":
        return renderQueryTypeFilterField(true);
      case "system_id":
        return renderSystemIdField(true);
      case "resource_type":
        return renderResourceTypeField(true);
      case "run_name":
        return renderRunNameField(true);
      case "domain_linkage_ids":
        return renderDomainLinkageIdsField(true);
      case "source_domain_id":
        return renderSourceDomainIdField(true);
      case "target_domain_id":
        return renderTargetDomainIdField(true);
      case "source_domain_key":
        return renderSourceDomainKeyField(true);
      case "target_domain_key":
        return renderTargetDomainKeyField(true);
      case "username":
        return renderUsernameField(true);
      case "email":
        return renderEmailField(true);
      case "role_name":
        return renderRoleNameField(true);
      case "group_name":
        return renderGroupNameField(true);
      case "tag_name":
        return renderTagNameField(true);
      case "system_name":
        return renderSystemNameField(true);
      case "connection_runtime_id":
        return renderConnectionRuntimeIdField(true);
      case "code":
        return renderCodeField(true);
      case "connection_type":
        return renderConnectionTypeField(true);
      case "run_instance_id":
        return renderRunInstanceIdField(true);
      case "issue_classification_name":
        return renderIssueClassificationNameField(true);
      case "reason_name":
        return renderReasonNameField(true);
      case "severity": // ✅ ADDED
        return renderSeverityField(true);
      case "end_date":
        return renderEndDateField();
      case "start_date":
        return renderStartDateField();
      default:
        return null;
    }
  };

  // Dynamic function to render dropdown fields (only non-primary fields)
  const renderDropdownField = (fieldName: string) => {
    // Only render if field is NOT in primaryFields
    if (primaryFields.includes(fieldName as any)) return null;

    switch (fieldName) {
      case "end_date":
        return renderEndDateField(true);
      case "start_date":
        return renderStartDateField(true);
      case "run_list":
        return renderRunListField();
      case "execution_sub_group_list":
        return renderExecutionSubGroupListField();
      case "assigned_by":
        return renderAssignedByField();
      case "assigned_user":
        return renderAssignedUserField();
      case "domain_list":
        return renderDomainListField();
      case "domain_name":
        return renderDomainNameField();
      case "resource_list":
        return renderResourceListField();
      case "rule_list":
        return renderRuleListField();
      case "comparison_research_query_id":
        return renderComparisonResearchQueryIdField();
      case "comparison_research_query_available":
        return renderComparisonResearchQueryAvailableField();
      case "status_filter":
        return renderStatusFilterField();
      case "query_type_filter":
        return renderQueryTypeFilterField();
      case "classification":
        return renderClassificationField();
      case "reason":
        return renderReasonField();
      case "rule":
        return renderRuleField();
      case "execution_id":
        return renderExecutionIdField();
      case "rule_id":
        return renderRuleIdField();
      case "rule_name":
        return renderRuleNameField();
      case "status":
        return renderStatusField();
      case "generic_research_id":
        return renderGenericResearchIdField();
      case "generic_research_code":
        return renderGenericResearchCodeField();
      case "generic_execution_id":
        return renderGenericExecutionIdField();
      case "execution_status":
        return renderExecutionStatusField();
      case "query_name":
        return renderQueryNameField();
      case "name": // ✅ NEW: Resource column name search
        return renderNameField();
      case "resource_column_filter_ids": // ✅ NEW: Resource column filter IDs
        return renderResourceColumnFilterIdsField();
      case "generic_research_query_id":
        return renderGenericResearchQueryIdField();
      case "system_id":
        return renderSystemIdField();
      case "resource_type":
        return renderResourceTypeField();
      case "run_name":
        return renderRunNameField();
      case "domain_linkage_ids":
        return renderDomainLinkageIdsField();
      case "source_domain_id":
        return renderSourceDomainIdField();
      case "target_domain_id":
        return renderTargetDomainIdField();
      case "source_domain_key":
        return renderSourceDomainKeyField();
      case "target_domain_key":
        return renderTargetDomainKeyField();
      case "username":
        return renderUsernameField();
      case "email":
        return renderEmailField();
      case "role_name":
        return renderRoleNameField();
      case "group_name":
        return renderGroupNameField();
      case "tag_name":
        return renderTagNameField();
      case "system_name":
        return renderSystemNameField();
      case "connection_runtime_id":
        return renderConnectionRuntimeIdField();
      case "code":
        return renderCodeField();
      case "connection_type":
        return renderConnectionTypeField();
      case "run_instance_id":
        return renderRunInstanceIdField();
      case "issue_classification_name":
        return renderIssueClassificationNameField();
      case "reason_name":
        return renderReasonNameField();
      case "severity": // ✅ ADDED
        return renderSeverityField();
      default:
        return null;
    }
  };

  // Render the "More" dropdown content
  const renderMoreDropdownContent = () => {
    return (
      <Box sx={{ padding: "0px", minWidth: "300px", maxWidth: "400px" }}>
        {requiredFields.map((fieldName) => renderDropdownField(fieldName))}
      </Box>
    );
  };

  return (
    <div
      ref={filterSidebarRef}
      className={`horizontal-filter-search ${className}`}
      style={{}}
    >
      <div className="filter-body">
        <div className="filter-fields-container">
          <Grid container rowSpacing={1.5} columnSpacing={2.5}>
            {/* Primary visible fields - dynamic based on primaryFields prop */}
            {primaryFields.map((fieldName) => renderPrimaryField(fieldName))}

            {/* More dropdown button */}
            {moreFields.length > 0 && (
              <Grid
                item
                xs={12}
                sm={4}
                md={largeWidth ? 4 : 3}
                lg={largeWidth ? 3 : 2}
                xl={largeWidth ? 3 : 2}
              >
                <div className="filter-field-group more-filters">
                  <div className="filter-field">
                    <div className="filter-input">
                      <Tooltip
                        title={t("search.moreFilterOptions")}
                        placement="top"
                        arrow
                      >
                        <IconButton
                          onClick={handleMoreMenuOpen}
                          className="more-filters-btn"
                          aria-label={t("search.moreFilterOptions")}
                          data-testid="more-filters-btn"
                          sx={{
                            border: "1px solid #e0e0e0",
                            borderRadius: "4px",
                            padding: "8px",
                            width: "100%",
                            justifyContent: "flex-start",
                            "&:hover": {
                              backgroundColor: "rgba(0, 0, 0, 0.04)",
                            },
                          }}
                        >
                          <Badge
                            color="primary"
                            variant="dot"
                            invisible={!hasMoreFieldValues}
                            sx={{
                              "& .MuiBadge-dot": {
                                backgroundColor: "var(--auto-green)",
                              },
                            }}
                          >
                            <IconMenus />
                          </Badge>
                          <Box sx={{ marginLeft: "8px", fontSize: "14px" }}>
                            More Filters
                            {/* {hasMoreFieldValues && (
                              <span
                                style={{
                                  color: "var(--auto-green)",
                                  marginLeft: "4px",
                                }}
                              >
                                (Active)
                              </span>
                            )} */}
                          </Box>
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              </Grid>
            )}

            {/* Action buttons */}
            <Grid
              item
              xs
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "flex-start",
                columnGap: "16px",
              }}
            >
              <Button
                variant="contained"
                color="secondary"
                className="btn-orange"
                onClick={handleSubmitFilter}
              >
                Apply Filter
              </Button>

              <Button
                variant="outlined"
                className="btn-orange btn-border cursor-pointer"
                onClick={handleClearFilter}
                color="secondary"
              >
                Clear Filter
              </Button>
            </Grid>
          </Grid>
        </div>
      </div>

      {/* More filters dropdown menu */}
      <Menu
        className="more-filters-menu"
        anchorEl={moreMenuAnchorEl}
        open={Boolean(moreMenuAnchorEl)}
        onClose={handleMoreMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            sx: {
              maxHeight: "400px",
              overflow: "auto",
            },
          },
        }}
      >
        <MenuItem
          disableRipple
          sx={{ "&:hover": { backgroundColor: "transparent" } }}
        >
          {renderMoreDropdownContent()}
        </MenuItem>
      </Menu>
    </div>
  );
};

export default SearchFilterBar;
